import { Injectable } from '@angular/core';
import { MainMapService } from '../../../../shared/service/main-map.service';
import { MessageService } from '../../../../core/service/message.service';
import { DetailViewFormBuilderService } from '../../../../shared/service/detail-view-form-builder.service';
import { RelationshipService } from '../../../../shared/service/relationship.service';
import { DetailViewFeatureParams, RelatedFeatureParams } from '../../detail-view.model';
import { AttributesService } from '../../../../shared/service/attributes.service';
import { EsriGraphic } from 'src/app/config/esri.module';
import { Observable, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { RelatedTableConfig } from '../../../../config/model/relationship-config.model';
import { CONSTANTS_VALUE } from '../../../../config/constants.module';

@Injectable({
  providedIn: 'root',
})
export class RelatedTableActionsService {
  private _relatedRelationshipInfo: any;
  private _relationshipInfo: any;

  constructor(
    private _mainMapService: MainMapService,
    private _messageService: MessageService,
    public detailViewFormBuilderService: DetailViewFormBuilderService,
    private _relationshipService: RelationshipService,
    private _attributesService: AttributesService
  ) {}

  get relatedRelationshipInfo() {
    return this._relatedRelationshipInfo;
  }

  set relatedRelationshipInfo(relatedRelationInfo) {
    this._relatedRelationshipInfo = relatedRelationInfo;
  }

  get relationshipInfo() {
    return this._relationshipInfo;
  }

  set relationshipInfo(relationInfo) {
    this._relationshipInfo = relationInfo;
  }

  /**
   * @name create
   * @description creates new related feature
   * @param relatedTableConfig
   * @param relatedFeatureParams
   */
  public create(relatedTableConfig: RelatedTableConfig, relatedFeatureParams: RelatedFeatureParams): Observable<any> {
    // create feature
    const relatedFeatureProperties: __esri.GraphicProperties = { attributes: {} };
    const relatedFeature: __esri.Graphic = new EsriGraphic(relatedFeatureProperties);
    // det default attributes

    return this._attributesService.getDefaultAttributes(relatedTableConfig.id, relatedFeature).pipe(
      mergeMap(attributes => {
        if (!relatedTableConfig.statisticDefinition) {
          return of({ newRelatedFeature: relatedFeature, newAttributes: attributes });
        } else {
          const _staticField = relatedTableConfig.statisticDefinition.statisticField;
          const _staticType = relatedTableConfig.statisticDefinition.statisticType;

          return this._relationshipService
            .getNewNumberForField(relatedFeatureParams.relatedLayerUrl, _staticField, _staticType)
            .pipe(
              map(statisticValue => {
                attributes[_staticField] = statisticValue;
                return { newRelatedFeature: relatedFeature, newAttributes: attributes };
              })
            );
        }
      })
    );
  }

  /**
   * @name save
   * @description saves selected feature
   */
  public save(
    featureParams: DetailViewFeatureParams,
    relatedFeatures: __esri.Graphic[],
    selectedRelatedFeature: __esri.Graphic,
    isCreateMode: boolean,
    isManyToMany: boolean
  ) {
    // prepare related feature for save
    const featureToBeSaved = this.getWriteActionForFeature(
      isCreateMode,
      isManyToMany,
      featureParams,
      selectedRelatedFeature
    );
    // save related feature
    this._relationshipService
      .saveRelatedFeatures(
        featureParams.featureLayerUrl,
        this.relationshipInfo,
        featureToBeSaved.adds,
        featureToBeSaved.updates
      )
      .subscribe(result => {
        if (isCreateMode && result.data.addResults.length === 1) {
          this.updateFeatureIds(result, featureParams, selectedRelatedFeature);
          // save relationship if many-to-many
          this.createRelatedFeatureManyToMany(isManyToMany, relatedFeatures, selectedRelatedFeature, featureParams);
          // onToMany
          this.unshiftRelatedFeature(isManyToMany, relatedFeatures, selectedRelatedFeature);
        } else if (result.data.updateResults.length === 1) {
          this._mainMapService.setCursor(CONSTANTS_VALUE.CURSOR.default);
          this._messageService.success('NOTIFICATIONS.SAVE_SUCCESS');
        }
      });
  }

  /**
   * @param relatedFeatureParams
   */
  public mapRelatedFeatureAttributesInCorrectType(relatedFeatureParams: RelatedFeatureParams) {
    try {
      // set attributes in the correct field type
      this.detailViewFormBuilderService.mapAttributesInCorrectFieldType(
        relatedFeatureParams.relatedFields,
        relatedFeatureParams.feature.attributes
      );
    } catch (e) {
      throw new Error('maping related feature attributes in correct type failt!');
    }
  }

  /**
   * @name updateFeatureIds
   * @param result: Result from applyEdit
   * @param featureParams
   * @param selectedRelatedFeature
   * @description updates feature objectId and globalId
   */
  private updateFeatureIds(result: any, featureParams: DetailViewFeatureParams, selectedRelatedFeature) {
    // update object-id
    if (featureParams.layerInfos.objectIdField) {
      selectedRelatedFeature.attributes[featureParams.layerInfos.objectIdField] = result.data.addResults[0].objectId;
    }
    // update global-id
    if (featureParams.layerInfos.globalIdField) {
      selectedRelatedFeature.attributes[featureParams.layerInfos.globalIdField] = result.data.addResults[0].globalId;
    }
  }

  /**
   * @name deleteRelatedFeature
   * @param relatedFeature
   * @param featureParams
   * @description deletes related feature and his relationship features
   */
  public deleteRelatedFeature(relatedFeature: __esri.Graphic, featureParams: DetailViewFeatureParams) {
    const oids: number[] = [relatedFeature.attributes.OBJECTID];
    this._relationshipService
      .deleteRelatedFeatures(featureParams.featureLayerUrl, this.relationshipInfo, oids)
      .subscribe(() => {
        this._messageService.success('NOTIFICATIONS.DELETE_SUCCESS');
      });
  }

  /**
   * @name getRelatedTablefkLayer
   * @param relationshipId
   * @param featureParams
   * @description  return related table fklayer id as string
   */
  public getRelatedTablefkLayer(relationshipId, featureParams: DetailViewFeatureParams): string {
    const relationshipInfo = this._relationshipService.getRelationshipInfoOfLayerInfo(
      featureParams.layerInfos,
      relationshipId
    );
    const fkRelationTable = featureParams.relatedTableConfigs.find(f => {
      return f.relationshipName === relationshipInfo.name;
    });
    if (fkRelationTable) return fkRelationTable.relationshipName;
  }

  /**
   * @param isCreateMode
   * @param isManyToMany
   * @param featureParams
   * @param selectedRelatedFeature
   * @description return action object depending on add or update
   */
  private getWriteActionForFeature(
    isCreateMode: boolean,
    isManyToMany: boolean,
    featureParams: DetailViewFeatureParams,
    selectedRelatedFeature: __esri.Graphic
  ): any {
    let adds, updates;
    if (isCreateMode) {
      // set relation directly on the related feature
      if (!isManyToMany) {
        const fkField: string = this.relatedRelationshipInfo.keyField;
        selectedRelatedFeature.attributes[fkField] = featureParams.feature.attributes[this.relationshipInfo.keyField];
      }
      adds = [selectedRelatedFeature];
    } else {
      updates = [selectedRelatedFeature];
    }
    return { adds, updates };
  }

  /**
   * @param isManyToMany
   * @param relatedFeatures
   * @param selectedRelatedFeature
   * @param featureParams
   * @description create feature in many to many relation
   */
  private createRelatedFeatureManyToMany(
    isManyToMany: boolean,
    relatedFeatures: __esri.Graphic[],
    selectedRelatedFeature: __esri.Graphic,
    featureParams: DetailViewFeatureParams
  ) {
    // save relationship if many-to-many
    if (!isManyToMany) return;
    const idRelatedFeature: string = selectedRelatedFeature.attributes[this.relatedRelationshipInfo.keyField];
    const idFeature: string = featureParams.feature.attributes[this.relationshipInfo.keyField];
    return this._relationshipService
      .createRelationshipFeature(
        featureParams.featureLayerUrl,
        this.relationshipInfo,
        this.relatedRelationshipInfo,
        idFeature,
        idRelatedFeature
      )
      .subscribe(() => {
        relatedFeatures.unshift(selectedRelatedFeature);
        this._mainMapService.setCursor(CONSTANTS_VALUE.CURSOR.default);
        this._messageService.success('NOTIFICATIONS.SAVE_SUCCESS');
      });
  }

  /**
   * @param isManyToMany
   * @param relatedFeatures
   * @param selectedRelatedFeature
   */
  private unshiftRelatedFeature(
    isManyToMany: boolean,
    relatedFeatures: __esri.Graphic[],
    selectedRelatedFeature: __esri.Graphic
  ) {
    if (isManyToMany) return;
    relatedFeatures.unshift(selectedRelatedFeature);
    this._mainMapService.setCursor(CONSTANTS_VALUE.CURSOR.default);
    this._messageService.success('NOTIFICATIONS.SAVE_SUCCESS');
  }
}
